import { verificationTypes } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Shield, Star, AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { PageContainer } from "@/components/layout/PageContainer";

export default function VerificationInfo() {
  useEffect(() => {
    document.title = "OZK 신뢰마크 안내 | OZK News";
  }, []);

  return (
    <PageContainer size="read">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary/5 to-background border-b -mx-4 px-4 py-12 mb-12">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="h-12 w-12 text-primary" />
            </div>
            <h1 className="text-4xl font-bold">OZK 신뢰마크란?</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              믿을 수 있는 업체를 쉽게 찾을 수 있도록 OZK News가 검증한 업체 인증 시스템입니다
            </p>
          </div>
        </div>

      <div className="space-y-12">
        {/* Why Verification Matters */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-primary" />
                왜 인증이 중요한가요?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                해외 생활에서 신뢰할 수 있는 한인 업체를 찾기란 쉽지 않습니다.
                OZK 신뢰마크는 실제 사업자 확인, 고객 만족도 검증, 파트너십 검증을 통해
                안심하고 이용할 수 있는 업체를 구분해드립니다.
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="bg-primary/5 rounded-lg p-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600 mb-2" />
                  <h4 className="font-semibold mb-1">실제 사업체 확인</h4>
                  <p className="text-sm text-muted-foreground">ABN 등록으로 검증된 실제 운영 중인 사업체</p>
                </div>
                <div className="bg-primary/5 rounded-lg p-4">
                  <Star className="h-8 w-8 text-blue-600 mb-2" />
                  <h4 className="font-semibold mb-1">고객 만족도 검증</h4>
                  <p className="text-sm text-muted-foreground">실제 이용 고객의 리뷰로 검증된 서비스 품질</p>
                </div>
                <div className="bg-primary/5 rounded-lg p-4">
                  <Shield className="h-8 w-8 text-yellow-600 mb-2" />
                  <h4 className="font-semibold mb-1">파트너십 검증</h4>
                  <p className="text-sm text-muted-foreground">OZK News와 계약을 맺은 공식 파트너</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* 3-Tier Verification System */}
        <section className="space-y-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-2">3단계 인증 시스템</h2>
            <p className="text-muted-foreground">
              업체의 신뢰도에 따라 3단계로 구분된 인증 마크를 제공합니다
            </p>
          </div>

          <div className="space-y-6">
            {verificationTypes.map((verification, index) => (
              <Card key={verification.id} className="overflow-hidden" data-testid={`verification-card-${verification.id}`}>
                <CardHeader className="bg-gradient-to-r from-primary/5 to-transparent">
                  <div className="flex items-start gap-4">
                    <div className="text-5xl flex-shrink-0">{verification.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-2xl">{verification.nameKo}</CardTitle>
                        <Badge variant="outline">{verification.name}</Badge>
                      </div>
                      <CardDescription className="text-base">
                        {verification.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    인증 조건
                  </h4>
                  <ul className="space-y-2">
                    {verification.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-primary mt-1">•</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Special notes for each tier */}
                  {verification.id === "basic" && (
                    <div className="mt-4 bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                      <p className="text-sm">
                        <strong className="text-green-700 dark:text-green-400">✓ 기본 신뢰 보장:</strong>{" "}
                        모든 업체는 ABN 확인을 통해 실제 사업체임이 검증됩니다.
                      </p>
                    </div>
                  )}

                  {verification.id === "premium" && (
                    <div className="mt-4 bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                      <p className="text-sm">
                        <strong className="text-blue-700 dark:text-blue-400">⭐ 자동 평가:</strong>{" "}
                        리뷰 수, 평점, 운영 기간이 조건을 충족하면 자동으로 발급됩니다.
                      </p>
                    </div>
                  )}

                  {verification.id === "partner" && (
                    <div className="mt-4 bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/20">
                      <p className="text-sm">
                        <strong className="text-yellow-700 dark:text-yellow-400">🤝 공식 파트너:</strong>{" "}
                        OZK News와 계약을 맺고 엄격한 검증 절차를 거친 업체입니다.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How to Use */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>인증 마크 활용 방법</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-bold text-primary">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">업체 목록에서 확인</h4>
                    <p className="text-sm text-muted-foreground">
                      교민 업체주소록, 핫샵&딜 등 업체 목록에서 인증 마크를 확인하세요.
                      마크에 마우스를 올리면 자세한 설명을 볼 수 있습니다.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-bold text-primary">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">필터 기능 사용</h4>
                    <p className="text-sm text-muted-foreground">
                      "인증 업체만 보기" 필터를 사용하여 검증된 업체만 빠르게 찾을 수 있습니다.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-bold text-primary">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">업체 상세 페이지</h4>
                    <p className="text-sm text-muted-foreground">
                      업체 상세 페이지 상단에 "이 업체는 인증된 사업체입니다" 배너가 표시됩니다.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-bold text-primary">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">문제 신고</h4>
                    <p className="text-sm text-muted-foreground">
                      인증 업체라도 문제가 있다면 신고 기능을 통해 알려주세요.
                      관리자가 검토하여 조치를 취합니다.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* For Business Owners */}
        <section>
          <Card className="border-primary/50">
            <CardHeader className="bg-primary/5">
              <CardTitle>업체 사업자를 위한 안내</CardTitle>
              <CardDescription>우리 업체도 인증을 받고 싶으신가요?</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">🟢 OZK Verified (기본 인증) 받는 방법</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-4">
                  <li>교민 업체주소록에 업체 등록</li>
                  <li>사업자등록번호(ABN) 입력</li>
                  <li>사업장 이미지, 웹사이트 등 추가 정보 제공</li>
                  <li>관리자 확인 대기 (1-3 영업일)</li>
                </ol>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">🔵 프리미엄 인증 받는 방법</h4>
                <p className="text-sm text-muted-foreground ml-4">
                  OZK Verified를 보유한 상태에서 고객 리뷰 5건 이상, 평균 평점 4.5점 이상,
                  3개월 이상 활동 시 자동으로 발급됩니다.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">🟡 파트너 인증 받는 방법</h4>
                <p className="text-sm text-muted-foreground ml-4">
                  OZK News와 유료 광고 또는 제휴 계약 시 발급됩니다.
                  자세한 사항은 <a href="/advertise" className="text-primary hover:underline">광고 문의</a>를 참고하세요.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Trust & Safety */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>안전한 거래를 위한 추가 팁</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>인증 마크가 있어도 항상 서비스 내용을 꼼꼼히 확인하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>다른 고객의 리뷰를 참고하여 종합적으로 판단하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>계약 전 가격, 서비스 범위 등을 명확히 확인하세요</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>문제 발생 시 신고 기능을 통해 커뮤니티를 보호하세요</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>
      </div>
    </PageContainer>
  );
}
